import { Box, Flex, Link, Text } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { ReactComponent as AlertIcon } from 'assets/icons/a11ymenu_alert.svg';
import { CTA_AI_TOOLS, Mixpanel } from 'libs/third-party/Mixpanel';
import { useTranslation } from 'react-i18next';
import { getInfographUri } from 'utils/getInfographUri';

import { DEFAULT_TOTAL_USER_CREDITS } from './Chat.config';

interface ChatCreditsInfoProps {
  credits?: number;
  showCreditsLeftMessage?: boolean;
  category?: string;
  subCategory?: string;
  lwoCategory?: string;
  lwoSubCategory?: string;
}

const CreditsInfoWrapper = styled(Flex)`
  p {
    font-size: 14px !important;
    margin: 0 !important;
  }

  a {
    color: var(--vg-colors-violet-500) !important;
  }

  @media screen and (max-width: 768px) {
    .creditsAmount {
      height: 40px;
      width: 100%;
    }

    .noCreditsText,
    .creditsLeftText {
      min-width: unset;
      width: 100%;
      flex-direction: column;
      padding: 8px 0;
      margin: 0;
      height: 100%;
    }
  }
`;

interface SignupLinkProps {
  text: string;
  onClick: () => void;
}

const SignupLink = ({ text, onClick }: SignupLinkProps) => (
  <Link
    cursor='pointer'
    paddingLeft={2}
    onClick={onClick}
    color='violet.500'
    textDecoration='underline'
    fontWeight='bold'
    borderRadius={2}
  >
    {text}
  </Link>
);

export const ChatCreditsInfo = ({
  credits,
  showCreditsLeftMessage,
  category,
  subCategory,
  lwoCategory,
  lwoSubCategory,
}: ChatCreditsInfoProps) => {
  const { t } = useTranslation('common_components', {
    useSuspense: false,
    keyPrefix: 'chat.promptInput',
  });

  const redirectToSignup = () => {
    const signupUrl = getInfographUri('/register');
    // Mixpanel event for tracking user signups
    Mixpanel.track(CTA_AI_TOOLS, {
      type: CTA_AI_TOOLS.type.creditsSignup,
    });

    const params = new URLSearchParams({
      ref: 'ai-tools',
    });

    if (!!category) {
      params.append('category', category);
    }

    if (!!subCategory) {
      params.append('subcategory', subCategory);
    }

    if (!!lwoCategory) {
      params.append('lwo_category', lwoCategory);
    }

    if (!!lwoSubCategory) {
      params.append('lwo_subcategory', lwoSubCategory);
    }

    window.location.href = signupUrl + '?' + params.toString();
  };

  return (
    <CreditsInfoWrapper className='creditsContainer'>
      {credits && credits > 0 ? (
        <>
          {showCreditsLeftMessage ? (
            <Box position='relative' w={{ base: '100%', md: 'fit-content' }}>
              <Box
                className='creditsLeftText'
                color='gray.600'
                bgColor='violet.50'
                h='40px'
                display='flex'
                alignItems='center'
                paddingLeft={2}
                paddingRight={2}
                fontSize='sm'
                borderRadius={2}
              >
                <Flex direction='row' justifyContent='center' alignItems='center'>
                  <AlertIcon
                    color='var(--vg-colors-violet-500)'
                    width='18'
                    height='18'
                    style={{ marginRight: '8px' }}
                  />
                  <Text color='font.500'>
                    {t('creditsLeft', { credits, totalCredits: DEFAULT_TOTAL_USER_CREDITS })}
                  </Text>
                </Flex>
                <SignupLink text={t('creditsSignUp')} onClick={redirectToSignup} />
              </Box>
            </Box>
          ) : (
            <Box className='creditsAmount' position='relative' w='100px' fontWeight='medium'>
              <Box color='gray.600' fontSize='0.9em' position='absolute' top={2} right={2} whiteSpace='nowrap'>
                {credits}/{DEFAULT_TOTAL_USER_CREDITS} {t('credits')}
              </Box>
            </Box>
          )}
        </>
      ) : (
        <Box
          className='noCreditsText'
          color='gray.600'
          bgColor='violet.50'
          h='40px'
          minW='391px'
          display='flex'
          alignItems='center'
          paddingLeft={2}
          marginTop={showCreditsLeftMessage ? 0 : 4}
          marginRight={showCreditsLeftMessage ? 0 : 4}
          fontSize='sm'
          borderRadius={2}
        >
          <Flex direction='row' justifyContent='center' alignItems='center'>
            <AlertIcon color='var(--vg-colors-violet-500)' width='18' height='18' style={{ marginRight: '8px' }} />
            <Text color='font.500'>{t('noCredits')}</Text>
          </Flex>
          <SignupLink text={t('creditsSignUp')} onClick={redirectToSignup} />
        </Box>
      )}
    </CreditsInfoWrapper>
  );
};
